import {
  OneSchemaTemplateColumnToUpdate,
  OneSchemaTemplateOverrides,
} from '@oneschema/react';

import DatadogService from '@services/DatadogService';
import { convertToTranslatedKeys } from '@utils/translation';
import { CLIENT_I18N_KEY, normalizeLabel } from '@features/event-level-column-rename/constants';
import { createColumnLabelResolver } from '@features/event-level-column-rename/utils/resolve-column-label';
import type {
  CanonicalColumnId,
  LineItemColumnLabels,
  TranslateLabel,
} from '@features/event-level-column-rename/types';

/**
 * OneSchema import template column key (as `getTemplateOverrides` configures the
 * `line_item_import_tlc` template) to canonical id. Extend only with a verified pairing —
 * a guessed entry compiles, passes its own tests and matches the wrong column.
 */
export const UPLOAD_TEMPLATE_COLUMN_CANONICAL_IDS = {
  line_item_name: 'lineItemName',
  negotiation_starting_price: 'negotiationPricePerUnit',
  price_per_unit: 'pricePerUnit',
  quantity_of_units: 'quantityOfUnits',
  unit_of_measure: 'unitOfMeasure',
} as const satisfies Record<string, CanonicalColumnId>;

type UploadTemplateColumnKey = keyof typeof UPLOAD_TEMPLATE_COLUMN_CANONICAL_IDS;

/**
 * Template keys that exist on the OneSchema platform template itself, so a rename may be applied
 * as a fresh `columns_to_update` entry even when no branch of `getTemplateOverrides` touched the
 * column. The other mapped keys are added client-side behind flags — when no entry for them
 * exists, the column is not part of this import and must not be conjured up by a rename.
 */
const PLATFORM_TEMPLATE_KEYS: readonly UploadTemplateColumnKey[] = [
  'line_item_name',
  'price_per_unit',
  'unit_of_measure',
];

/**
 * A surface may print a wording of its own for a column's default header. A sheet downloaded
 * with no override carries that wording, so it must keep auto-matching after the template column
 * is relabeled. The buyer export prints t('general.name') for the line-item-name column
 * (`getTranslatedDefaultHeadersMap`).
 */
const SURFACE_DEFAULT_I18N_KEYS: Partial<Record<UploadTemplateColumnKey, string>> = {
  line_item_name: 'general.name',
};

/**
 * OneSchema accepts `alternative_names` at runtime but the SDK type doesn't declare it.
 * https://docs.oneschema.co/docs/per-customer-overrides-v2
 */
interface TemplateColumnOverride {
  alternative_names?: string[];
  key?: string;
  label?: string;
}

const reportedCollisionIds = new Set<CanonicalColumnId>();

/**
 * Reports a stored label that eventLabelCollidesWithAnotherColumn with another mapped column's name — pre-existing data the
 * write path would now reject.
 *
 * Logs at most once per column id per page load: the overrides are rebuilt on every render of
 * the import flow, so an unguarded log would bury the very signal it exists to raise.
 */
const reportLabelCollision = (columnId: CanonicalColumnId, templateKey: string): void => {
  if (reportedCollisionIds.has(columnId)) return;

  reportedCollisionIds.add(columnId);
  DatadogService.captureErrorMessage(
    'Event column label eventLabelCollidesWithAnotherColumn with another column\'s name; upload auto-matching left untouched for the colliding column',
    { columnId, templateKey }
  );
};

/** Test seam: clears the once-per-id log guard so each case starts from a known state. */
export const resetLabelCollisionReports = (): void => {
  reportedCollisionIds.clear();
};

/** One column of an upload surface: its canonical id plus the default header wordings that surface prints. */
export interface UploadColumnHeaderDefaults {
  /** Absent for a column no rename surface knows (Logistics Charge); it then wears org/default names only. */
  canonicalId?: CanonicalColumnId;
  defaultHeaderNames: string[];
}

export interface AcceptedUploadColumnHeaderNames {
  /** Every wording a sheet may carry for the column: the effective name, the org label and the defaults. */
  acceptedHeaderNames: string[];
  eventLabel?: string;
  /** The event override's normalised label equals another column's name — matching must not guess. */
  eventLabelCollidesWithAnotherColumn: boolean;
  /** What this event prints for the column. */
  printedHeaderName: string;
}

interface BuildAcceptedHeaderNamesByColumnParams<K extends string> {
  companyColumnNameOverrides?: Record<string, string> | null;
  headerDefaultsByColumn: Record<K, UploadColumnHeaderDefaults>;
  labels?: LineItemColumnLabels | null;
  t: TranslateLabel;
}

const dedupeHeaderNamesIgnoringCaseAndWhitespace = (names: string[]): string[] => names.filter((name, index) => (
  names.findIndex(other => normalizeLabel(other) === normalizeLabel(name)) === index
));

/**
 * The one reverse map every upload surface matches headers through: for each column, the name
 * this event prints (event label, else org label, else the surface's default — via the feature's
 * label resolver) plus every earlier wording a downloaded sheet may still carry, and whether the
 * event override eventLabelCollidesWithAnotherColumn with another column's name. Callers match header text against
 * `acceptedHeaderNames` and never against a label on its own.
 */
export const buildAcceptedHeaderNamesByColumn = <K extends string>({
  companyColumnNameOverrides,
  headerDefaultsByColumn,
  labels,
  t,
}: BuildAcceptedHeaderNamesByColumnParams<K>): Record<K, AcceptedUploadColumnHeaderNames> => {
  const columnKeys = Object.keys(headerDefaultsByColumn) as K[];
  const resolveColumnLabel = createColumnLabelResolver({ labels, t });
  const organisationLabelByDefaultHeaderName: Record<string, string> = convertToTranslatedKeys(t, companyColumnNameOverrides ?? {});

  const organisationLabelFor = ({ canonicalId, defaultHeaderNames }: UploadColumnHeaderDefaults): string | undefined => (
    (canonicalId ? companyColumnNameOverrides?.[CLIENT_I18N_KEY[canonicalId]] : undefined)
      ?? defaultHeaderNames.map(name => organisationLabelByDefaultHeaderName[name]).find(Boolean)
  );

  const acceptedHeaderNamesByColumn = Object.fromEntries(columnKeys.map(columnKey => {
    const column = headerDefaultsByColumn[columnKey];
    const eventLabel = (column.canonicalId && labels?.[column.canonicalId]) || undefined;
    const organisationLabel = organisationLabelFor(column);
    const printedHeaderName = eventLabel
      ?? organisationLabel
      ?? (column.canonicalId ? resolveColumnLabel(column.canonicalId, column.defaultHeaderNames[0]) : column.defaultHeaderNames[0]);

    return [columnKey, {
      acceptedHeaderNames: dedupeHeaderNamesIgnoringCaseAndWhitespace(
        [printedHeaderName, organisationLabel, ...column.defaultHeaderNames].filter((name): name is string => Boolean(name))
      ),
      eventLabel,
      eventLabelCollidesWithAnotherColumn: false,
      printedHeaderName,
    }];
  })) as Record<K, AcceptedUploadColumnHeaderNames>;

  columnKeys.forEach(columnKey => {
    const { eventLabel } = acceptedHeaderNamesByColumn[columnKey];

    if (!eventLabel) return;

    acceptedHeaderNamesByColumn[columnKey].eventLabelCollidesWithAnotherColumn = columnKeys.some(otherColumnKey => {
      if (otherColumnKey === columnKey) return false;

      const otherColumnHeaderNames = [
        acceptedHeaderNamesByColumn[otherColumnKey].printedHeaderName,
        ...headerDefaultsByColumn[otherColumnKey].defaultHeaderNames,
      ];
      return otherColumnHeaderNames.some(otherName => normalizeLabel(otherName) === normalizeLabel(eventLabel));
    });
  });

  return acceptedHeaderNamesByColumn;
};

const findColumnEntry = (
  overrides: OneSchemaTemplateOverrides,
  templateKey: UploadTemplateColumnKey
): { entry: TemplateColumnOverride; list: 'columns_to_add' | 'columns_to_update' } | undefined => {
  const updateEntry = overrides.columns_to_update
    ?.find(column => (column as TemplateColumnOverride).key === templateKey);
  if (updateEntry) return { entry: updateEntry as TemplateColumnOverride, list: 'columns_to_update' };

  const addEntry = overrides.columns_to_add
    ?.find(column => (column as TemplateColumnOverride).key === templateKey);
  if (addEntry) return { entry: addEntry as TemplateColumnOverride, list: 'columns_to_add' };

  return undefined;
};

interface ApplyColumnLabelsToTemplateOverridesParams {
  companyColumnNameOverrides?: Record<string, string> | null;
  labels?: LineItemColumnLabels | null;
  overrides: OneSchemaTemplateOverrides;
  t: TranslateLabel;
}

/**
 * Applies the event's column-label map to a fully built set of OneSchema template overrides, as
 * the final step — after every branch of `getTemplateOverrides` has said its piece.
 *
 * Matching stays keyed on the stable template column key throughout: this only changes what a
 * column is *called* in the mapper, never which key its values land under. For each renamed
 * column the current label moves into `alternative_names` alongside the platform default and the
 * organisation label (`companyColumnNameOverrides`, keyed the same way as `CLIENT_I18N_KEY` — the
 * client's i18n key for the column), so a sheet headed with the default name, the organisation
 * name, or the current event name all auto-match the same column. The organisation name is
 * carried explicitly because `labels` is already event-over-org merged: once an event override
 * exists, the org wording is no longer readable from `labels` at all, even though a sheet
 * downloaded before the event override was set still carries it.
 *
 * An override whose normalised label eventLabelCollidesWithAnotherColumn with another mapped column's effective name is not
 * applied at all — auto-matching would have to guess which column owns the header, and a wrong
 * guess silently binds values to the wrong column. The colliding header instead surfaces
 * unmatched, by name, in the mapper, and the collision is reported. The write path rejects such
 * labels, so this guards pre-existing data only.
 *
 * An absent map leaves the overrides untouched — that is an older backend, not a fault.
 */
export const applyColumnLabelsToTemplateOverrides = ({
  companyColumnNameOverrides,
  labels,
  overrides,
  t,
}: ApplyColumnLabelsToTemplateOverridesParams): OneSchemaTemplateOverrides => {
  if (!labels) return overrides;

  const templateKeys = Object.keys(UPLOAD_TEMPLATE_COLUMN_CANONICAL_IDS) as UploadTemplateColumnKey[];
  const acceptedHeaderNamesByTemplateKey = buildAcceptedHeaderNamesByColumn({
    companyColumnNameOverrides,
    headerDefaultsByColumn: Object.fromEntries(templateKeys.map(templateKey => {
      const columnId = UPLOAD_TEMPLATE_COLUMN_CANONICAL_IDS[templateKey];
      const surfaceDefaultKey = SURFACE_DEFAULT_I18N_KEYS[templateKey];

      return [templateKey, {
        canonicalId: columnId,
        defaultHeaderNames: [t(CLIENT_I18N_KEY[columnId]), ...(surfaceDefaultKey ? [t(surfaceDefaultKey)] : [])],
      }];
    })) as Record<UploadTemplateColumnKey, UploadColumnHeaderDefaults>,
    labels,
    t,
  });

  const applied: OneSchemaTemplateOverrides = {
    ...overrides,
    columns_to_add: [...(overrides.columns_to_add ?? [])],
    columns_to_update: [...(overrides.columns_to_update ?? [])],
  };

  templateKeys.forEach(templateKey => {
    const columnId = UPLOAD_TEMPLATE_COLUMN_CANONICAL_IDS[templateKey];
    const { acceptedHeaderNames, eventLabelCollidesWithAnotherColumn, eventLabel } = acceptedHeaderNamesByTemplateKey[templateKey];

    if (!eventLabel) return;

    if (eventLabelCollidesWithAnotherColumn) {
      reportLabelCollision(columnId, templateKey);
      return;
    }

    const existing = findColumnEntry(applied, templateKey);
    const alternativeNames = dedupeHeaderNamesIgnoringCaseAndWhitespace([
      ...(existing?.entry.alternative_names ?? []),
      ...(existing?.entry.label ? [existing.entry.label] : []),
      ...acceptedHeaderNames,
    ]).filter(candidate => normalizeLabel(candidate) !== normalizeLabel(eventLabel));

    if (existing) {
      const relabeled = { ...existing.entry, alternative_names: alternativeNames, label: eventLabel };
      const list = applied[existing.list] as TemplateColumnOverride[];
      list[list.indexOf(existing.entry)] = relabeled;
      return;
    }

    if (!PLATFORM_TEMPLATE_KEYS.includes(templateKey)) return;

    applied.columns_to_update?.push({
      alternative_names: alternativeNames,
      key: templateKey,
      label: eventLabel,
    } as OneSchemaTemplateColumnToUpdate);
  });

  return applied;
};
